import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRoute, PATHS } from '@src/core/router/paths';

export type OpenRecordFn = (
  entity: string,
  id: string | number | undefined
) => void;

/**
 * Entities whose records have a navigable detail/edit route that is actually
 * mounted in the router (`src/core/router/index.tsx`):
 *   - `lead`        → SINGLE_LEAD          (`/leads/:leadId`)
 *   - `submission`  → SINGLE_SUBMISSION    (`/submissions/:submissionId`)
 *   - `participant` → EDIT_PARTICIPANT     (`/participants/edit/:participantId`)
 *
 * `invoice`, `payment` and `member` are intentionally NOT navigable in v1:
 * the router only mounts their LIST pages, not per-record detail/edit routes,
 * so a deep-link would land on the NotFound catch-all. Any unknown entity is
 * likewise a no-op. Add an entity here only once its detail route is mounted.
 */
const NAVIGABLE_ENTITIES = new Set(['lead', 'submission', 'participant']);

/** True when `useOpenRecord` can build a route for this entity. */
export function isNavigableEntity(entity: string): boolean {
  return NAVIGABLE_ENTITIES.has(entity);
}

/**
 * Build the org-relative path for a record. Returns null for non-navigable
 * entities. Note `createRoute` only prepends the org slug + query params — it
 * does NOT substitute `:id` placeholders — so paths are built concretely here,
 * using the PATHS function-forms where they exist (`SINGLE_SUBMISSION`,
 * `EDIT_PARTICIPANT`) and a literal string for the placeholder-template lead
 * route, mirroring existing call sites. Only entities with a mounted detail
 * route are handled here; everything else (invoice, payment, member, unknown)
 * returns null so the caller no-ops — see NAVIGABLE_ENTITIES above.
 */
function recordPath(entity: string, id: string | number): string | null {
  switch (entity) {
    case 'lead':
      return createRoute(`/leads/${id}`);
    case 'submission':
      return createRoute(PATHS.SINGLE_SUBMISSION(id));
    case 'participant':
      return createRoute(PATHS.EDIT_PARTICIPANT(id));
    default:
      return null;
  }
}

/**
 * Returns an imperative navigate function that opens a CRM record by
 * (entity, id). No-ops when the id is missing or the entity is not navigable,
 * so callers can invoke it unconditionally.
 */
export function useOpenRecord(): OpenRecordFn {
  const navigate = useNavigate();
  return useCallback(
    (entity, id) => {
      if (id === undefined || id === null || id === '') return;
      const path = recordPath(entity, id);
      if (!path) return;
      navigate(path);
    },
    [navigate]
  );
}
